import React, {useMemo} from 'react';

import {SINGLE_DATASET_OUTPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {DataFrame} from 'data-frames/DataFrameTypes';
import DataFrameTable, {DataFrameTableServerPaginationOptions} from 'data-frames/table/DataFrameTable';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import useDataFramePageOffsetState from 'data-frames/table/hooks/useDataFramePageOffsetState';
import useDataFrameTableMode from 'data-frames/table/hooks/useDataFrameTableMode';
import {useControlledDataFrameTableSelectionState} from 'data-frames/table/hooks/useDataFrameTableSelectionState';
import mergeDataFrameRenderOverrides from 'data-frames/table/mergeDataFrameRenderOverrides';
import {getAddToWorklistAction} from 'data-frames/toolbar/toolbar-actions/renderAddToWorklistOption';
import {getCopyToClipboardAction} from 'data-frames/toolbar/toolbar-actions/renderCopyToClipboardOption';
import {getDownloadAsCSVAction} from 'data-frames/toolbar/toolbar-actions/renderDownloadAsCSVOption';
import {getToggleChipsAction} from 'data-frames/toolbar/toolbar-actions/renderToggleChipsOption';
import getDataFramePaginationQueryVariables from 'data-frames/util/getDataFramePaginationQueryVariables';
import sortDataFrame from 'data-frames/util/sortDataFrame';
import toDataFrameSortInput, {DataFrameSortInputVariable} from 'data-frames/util/toDataFrameSortInput';
import {
  GetPersistedDataFrame,
  GetPersistedDataFrame_dataFrame_InaccessibleInfoNode as InaccessibleInfoGraphQL,
  GetPersistedDataFrameVariables,
} from 'data-frames/wrapper/__generated/GetPersistedDataFrame';
import {
  GET_PERSISTED_DATA_FRAME_QUERY,
  PersistedDataFrameGraphQL,
} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import usePrefetchPersistedDataFramePages from 'data-frames/wrapper/prefetchPersistedDataFramePages';
import useSelectedRowsAcrossPages from 'data-frames/wrapper/useSelectedRowsAcrossPages';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {assertPipelineStepRawDataIsKind, PipelineStep, pipelineStepIsStepType} from 'pipelines/PipelineTypes';
import {resolveOutputStepData} from 'pipelines/PipelineViewUtils';
import {getOutputStepDataRawDatas} from 'pipelines/StepDataUtils';
import useSimpleTableSortState from 'ui-library/simple-table/useSimpleTableSortState';
import {readableTime} from 'util/DateUtil';
import fuzzydate from 'util/fuzzydate';
import {capitalize, formatBytes} from 'util/NameUtil';

import getDataFrameWarning from './data-frame-view/getDataFrameWarning';
import DataViewContext, {combineDataViewWarnings, useDataViewContext} from './DataViewContext';
import DataViewPane from './DataViewPane';
import {FailedViewContent} from './FailedView';
import LoadingView from './LoadingView';

export interface DataFrameViewWithDataFrameProps {
  dataFrame: DataFrame;
  clientId: string;
  analyticsData?: {
    dataFrameId?: string;
  };
  serverPagination?: DataFrameTableServerPaginationOptions;
}

export interface DataFrameViewWithPersistedDataFrameProps {
  persistedDataFrame: PersistedDataFrameGraphQL;
  serverPagination?: DataFrameTableServerPaginationOptions;
}

export interface DataFrameViewWithDataFrameIdProps {
  dataFrameId: string;
}

export type DataFrameViewProps =
  | DataFrameViewWithDataFrameProps
  | DataFrameViewWithPersistedDataFrameProps
  | DataFrameViewWithDataFrameIdProps
  | Record<string, never>;

function isDataFrameViewWithDataFrame(props: DataFrameViewProps): props is DataFrameViewWithDataFrameProps {
  return props.hasOwnProperty('dataFrame');
}

function isDataFrameViewWithPersistedDataFrame(
  props: DataFrameViewProps
): props is DataFrameViewWithPersistedDataFrameProps {
  return props.hasOwnProperty('persistedDataFrame');
}

function isDataFrameViewWithDataFrameId(
  props: DataFrameViewProps
): props is DataFrameViewWithDataFrameIdProps {
  return props.hasOwnProperty('dataFrameId');
}

/**
 * Render view for data frames.
 *
 * Can be used in four ways:
 * 1. With a `dataFrame` prop: Renders the provided DataFrame object directly.
 * 2. With a `persistedDataFrame` prop: Renders the provided PersistedDataFrameGraphQL.
 * 3. With a `dataFrameId` prop: Queries and renders the data frame by ID.
 * 4. With no props: Uses analysisView from context.
 *
 * @example
 * // With DataFrame object
 * <DataFrameView dataFrame={dataFrameObject} clientId="my-client-id" />
 *
 * // With persisted data frame
 * <DataFrameView persistedDataFrame={persistedDataFrame} />
 *
 * // With data frame ID
 * <DataFrameView dataFrameId="df_abc123" />
 *
 * // With analysisView from DataViewContext
 * <DataFrameView />
 */
export default function DataFrameView(props: DataFrameViewProps = {}): JSX.Element {
  if (isDataFrameViewWithDataFrame(props)) {
    return (
      <DataFrameViewWithDataFrame
        dataFrame={props.dataFrame}
        clientId={props.clientId}
        analyticsData={props.analyticsData}
      />
    );
  }

  if (isDataFrameViewWithPersistedDataFrame(props)) {
    return <DataFrameViewWithPersistedDataFrame persistedDataFrame={props.persistedDataFrame} />;
  }

  if (isDataFrameViewWithDataFrameId(props)) {
    return <DataFrameViewWithDataFrameId dataFrameId={props.dataFrameId} />;
  }

  return <DataFrameViewWithAnalysisView />;
}

function DataFrameViewWithDataFrame({
  analyticsData: partialAnalyticsData,
  clientId,
  dataFrame,
  serverPagination,
}: DataFrameViewWithDataFrameProps): JSX.Element {
  const dataViewContextValue = useDataViewContext();
  const {
    actionInput,
    actionOrigin,
    dataFrameViewOptions: {
      dataFrameRenderOverrides,
      getAdditionalRenderOverrides,
      defaultTableMode = DataFrameTableMode.TEXT,
      headerMenuActions,
    },
    rowSelectionState: {selectedRowIndices, setSelectedRowIndices},
  } = dataViewContextValue;

  const analyticsData = useMemo(
    () => ({
      ...partialAnalyticsData,
      actionOrigin,
    }),
    [actionOrigin, partialAnalyticsData]
  );

  const {tableMode, setTableMode} = useDataFrameTableMode(clientId, defaultTableMode);

  const dataFrameTableSortOptions = useSimpleTableSortState(clientId);
  // `dataFrame` is already sorted server-side in server-paginated mode (it's only the current page, so
  // sorting it further client-side would be incorrect).
  const sortedDataFrame = useMemo(
    () =>
      serverPagination
        ? dataFrame
        : sortDataFrame(dataFrame, dataFrameTableSortOptions.sortState, DataFrameTableMode.TEXT),
    [dataFrame, dataFrameTableSortOptions.sortState, serverPagination]
  );

  const selectionStateHookOutput = useControlledDataFrameTableSelectionState(
    sortedDataFrame,
    new Set([...selectedRowIndices].map((index) => String(index))),
    (selectedRowIds: Set<string>) => {
      setSelectedRowIndices(new Set([...selectedRowIds].map((id) => Number(id))));
    }
  );

  // Bulk actions (copy/download/add to worklist) filter `dataFrame.rows` by `selectedRowIds`; in
  // server-paginated mode `sortedDataFrame` only holds the current page, so resolve any selected rows on
  // other pages before handing `dataFrame` to those actions. `DataFrameTable` below keeps rendering
  // `sortedDataFrame` unmodified.
  const {
    dataFrame: dataFrameForActions,
    isLoadingSelectedRows,
    hasErrorLoadingSelectedRows,
  } = useSelectedRowsAcrossPages(
    analyticsData.dataFrameId,
    sortedDataFrame,
    selectionStateHookOutput.selectedRowIds,
    serverPagination ? toDataFrameSortInput(dataFrameTableSortOptions.sortState) : null,
    serverPagination != null
  );

  const mergedDataFrameRenderOverrides = useMemo(() => {
    if (getAdditionalRenderOverrides == null) {
      return dataFrameRenderOverrides;
    }
    const additionalOverrides = getAdditionalRenderOverrides(sortedDataFrame.rows, dataFrame.numRows);
    return dataFrameRenderOverrides != null
      ? mergeDataFrameRenderOverrides(dataFrameRenderOverrides, additionalOverrides)
      : additionalOverrides;
  }, [dataFrame.numRows, dataFrameRenderOverrides, getAdditionalRenderOverrides, sortedDataFrame.rows]);

  const newDataFrameViewContextValue = useMemo(
    () => ({
      ...dataViewContextValue,
      actions: {
        ...dataViewContextValue.actions,
        overflowActions: [
          ...dataViewContextValue.actions.overflowActions,
          getCopyToClipboardAction(
            dataFrameForActions,
            analyticsData,
            selectionStateHookOutput.selectedRowIds,
            isLoadingSelectedRows,
            hasErrorLoadingSelectedRows
          ),
          getDownloadAsCSVAction(
            dataFrameForActions,
            analyticsData,
            selectionStateHookOutput.selectedRowIds,
            isLoadingSelectedRows,
            hasErrorLoadingSelectedRows
          ),
          getToggleChipsAction(analyticsData, tableMode, setTableMode),
          ...(BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS
            ? [
                getAddToWorklistAction(
                  dataFrameForActions,
                  analyticsData,
                  selectionStateHookOutput.selectedRowIds,
                  isLoadingSelectedRows,
                  hasErrorLoadingSelectedRows
                ),
              ]
            : []),
        ],
      },
      dataTestAttrs: {component: 'DataFrameView', element: 'root'},
    }),
    [
      analyticsData,
      dataFrameForActions,
      dataViewContextValue,
      hasErrorLoadingSelectedRows,
      isLoadingSelectedRows,
      selectionStateHookOutput,
      setTableMode,
      tableMode,
    ]
  );

  return (
    <DataViewContext.Provider value={newDataFrameViewContextValue}>
      <DataViewPane>
        <DataFrameTable
          actionOrigin={actionOrigin}
          dataFrame={sortedDataFrame}
          clientStateId={clientId}
          dataFrameRenderOverrides={mergedDataFrameRenderOverrides}
          headerMenuOptions={headerMenuActions?.(actionInput) ?? undefined}
          mode={tableMode}
          renderIsExcludedColumn={false}
          selectionOptions={{allowRowSelection: true, ...selectionStateHookOutput}}
          sortOptions={dataFrameTableSortOptions}
          serverPagination={serverPagination}
        />
      </DataViewPane>
    </DataViewContext.Provider>
  );
}

function DataFrameViewWithPersistedDataFrame({
  persistedDataFrame,
  serverPagination,
}: DataFrameViewWithPersistedDataFrameProps): JSX.Element {
  const dataViewContextValue = useDataViewContext();

  const newDataViewContextValue = useMemo(() => {
    const {metadataContent} = dataViewContextValue;

    const {allColumnsJson, dataFrameJson} = persistedDataFrame;

    return {
      ...dataViewContextValue,
      actionInput: {persistedDataFrame},
      metadataContent: {
        ...metadataContent,
        gridCells: [
          {component: persistedDataFrame.creator.name, label: 'Creator'},
          ...(persistedDataFrame.modifiedAt != null
            ? [{component: capitalize(fuzzydate(persistedDataFrame.modifiedAt)), label: 'Last modified'}]
            : []),
          ...(persistedDataFrame.createdAt != null
            ? [{component: readableTime(persistedDataFrame.createdAt), label: 'Created at'}]
            : []),
          {component: persistedDataFrame.numRows, label: 'Rows'},
          {
            component: persistedDataFrame.size ? formatBytes(persistedDataFrame.size) : 'N/A',
            label: 'Size',
          },
          ...metadataContent.gridCells,
        ],
      },
      warning: combineDataViewWarnings(
        dataViewContextValue.warning,
        getDataFrameWarning({
          renderedColumns: dataFrameJson.columns.length,
          // When server pagination is active, every row is reachable by paging, so nothing is truncated.
          renderedRows: serverPagination ? persistedDataFrame.numRows : dataFrameJson.rows.length,
          totalColumns: allColumnsJson.length,
          totalRows: dataFrameJson.numRows ?? dataFrameJson.rows.length,
        })
      ),
    };
  }, [persistedDataFrame, dataViewContextValue, serverPagination]);

  return (
    <DataViewContext.Provider value={newDataViewContextValue}>
      <DataFrameViewWithDataFrame
        analyticsData={{dataFrameId: persistedDataFrame.id}}
        clientId={persistedDataFrame.id}
        dataFrame={persistedDataFrame.dataFrameJson}
        serverPagination={serverPagination}
      />
    </DataViewContext.Provider>
  );
}

function DataFrameViewWithDataFrameId({dataFrameId}: DataFrameViewWithDataFrameIdProps): JSX.Element {
  // Must match the `clientId` passed to `DataFrameViewWithDataFrame` below (`persistedDataFrame.id`, which
  // equals `dataFrameId`), since that's the key its `useSimpleTableSortState` and pagination hooks read.
  const {rowsOffset, onChangeRowsOffset} = useDataFramePageOffsetState(dataFrameId);
  const sortOptions = useSimpleTableSortState(dataFrameId);
  const {useServerPagination, limit, offset, sortInput} = getDataFramePaginationQueryVariables({
    rowsOffset,
    sortInput: toDataFrameSortInput(sortOptions.sortState),
  });

  return (
    <BenchlingQuery<GetPersistedDataFrame, GetPersistedDataFrameVariables>
      query={GET_PERSISTED_DATA_FRAME_QUERY}
      renderLoading={() => <LoadingView />}
      variables={{dataFrameId, limit, offset, sortInput}}
    >
      {({dataFrame: persistedDataFrame}) => (
        <DataFrameViewWithPersistedDataFrameAndPrefetch
          dataFrameId={dataFrameId}
          persistedDataFrame={persistedDataFrame}
          useServerPagination={useServerPagination}
          rowsOffset={rowsOffset}
          onChangeRowsOffset={onChangeRowsOffset}
          sortInput={sortInput}
        />
      )}
    </BenchlingQuery>
  );
}

/**
 * Prefetches neighboring pages and renders the persisted data frame, split out from
 * `DataFrameViewWithDataFrameId`/`DataFrameViewWithAnalysisView` so `usePrefetchPersistedDataFramePages` is
 * called unconditionally on every render of this component (it's inside `BenchlingQuery`'s `children`
 * render prop, which — unlike `renderLoading` — is only invoked once loaded; hooks can't live in code that
 * doesn't run on every render of the same component).
 */
function DataFrameViewWithPersistedDataFrameAndPrefetch({
  dataFrameId,
  persistedDataFrame,
  useServerPagination,
  rowsOffset,
  onChangeRowsOffset,
  sortInput,
}: {
  dataFrameId: string;
  persistedDataFrame: PersistedDataFrameGraphQL | InaccessibleInfoGraphQL;
  useServerPagination: boolean;
  rowsOffset: number;
  onChangeRowsOffset: (rowsOffset: number) => void;
  sortInput: DataFrameSortInputVariable | null;
}): JSX.Element {
  const dataFrameIsAccessible = !isInaccessible(persistedDataFrame);
  usePrefetchPersistedDataFramePages(
    dataFrameId,
    rowsOffset,
    dataFrameIsAccessible ? persistedDataFrame.numRows : 0,
    sortInput,
    useServerPagination && dataFrameIsAccessible
  );
  return dataFrameIsAccessible ? (
    <DataFrameViewWithPersistedDataFrame
      persistedDataFrame={persistedDataFrame}
      serverPagination={
        useServerPagination
          ? {rowsOffset, onChangeRowsOffset, totalRows: persistedDataFrame.numRows}
          : undefined
      }
    />
  ) : (
    <FailedViewContent statusMessage="You do not have access to this data frame." />
  );
}

/** @package */
function DataFrameViewWithAnalysisView(): JSX.Element {
  const dataViewContextValue = useDataViewContext();
  const {analysisView, previewData, steps} = dataViewContextValue;

  assert(analysisView != null, 'analysisView must be provided in context when using DataFrameView');

  const lastNode = analysisView.lineage[analysisView.lineage.length - 1];
  const stepOutputReference = lastNode.stepOutputReference!;
  const stepsArray = steps ?? ([] as ReadonlyArray<PipelineStep>);
  const outputStepData = resolveOutputStepData(stepsArray, {
    step: lastNode.step,
    stepOutputReference,
  });

  const dataFrame =
    previewData && pipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, previewData.draftStep)
      ? getOutputStepDataRawDatas<PipelineStepType.TABLE_VIEW, 0>(
          previewData.draftStep,
          SINGLE_DATASET_OUTPUT_STEP_DATA_NAME
        )[0]
      : outputStepData.rawDatas[stepOutputReference.rawDataIndex!];

  assertPipelineStepRawDataIsKind(dataFrame, PipelineStepDataKind.DATASET);

  // Must match the `clientId` passed to `DataFrameViewWithDataFrame` below (`persistedDataFrame.id`, which
  // equals `dataFrame.id`), since that's the key its `useSimpleTableSortState` and pagination hooks read.
  const {rowsOffset, onChangeRowsOffset} = useDataFramePageOffsetState(dataFrame.id);
  const sortOptions = useSimpleTableSortState(dataFrame.id);
  const {useServerPagination, limit, offset, sortInput} = getDataFramePaginationQueryVariables({
    rowsOffset,
    sortInput: toDataFrameSortInput(sortOptions.sortState),
  });

  return (
    <BenchlingQuery<GetPersistedDataFrame, GetPersistedDataFrameVariables>
      query={GET_PERSISTED_DATA_FRAME_QUERY}
      renderLoading={() => <LoadingView />}
      variables={{dataFrameId: dataFrame.id, limit, offset, sortInput}}
    >
      {({dataFrame: persistedDataFrame}) => {
        assertAndContinue(
          !isInaccessible(persistedDataFrame),
          'Expected users to have access to all tables in analyses.'
        );
        return (
          <DataFrameViewWithPersistedDataFrameAndPrefetch
            dataFrameId={dataFrame.id}
            persistedDataFrame={persistedDataFrame}
            useServerPagination={useServerPagination}
            rowsOffset={rowsOffset}
            onChangeRowsOffset={onChangeRowsOffset}
            sortInput={sortInput}
          />
        );
      }}
    </BenchlingQuery>
  );
}
